import { useContext, useEffect, useState } from 'react';
import 'react-circular-progressbar/dist/styles.css';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import { getScoreSurfaceLabel } from './complianceUtils';
import { scoreToGrade } from './complianceGrades';

// The shared scale (complianceGrades.js); re-exported for existing importers.
export { scoreToGrade };

const ComplianceScore = ({
  score,
  height,
  feedback = [],
  criteriaSource = 'cms',
}) => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const statsData = await apiClient.getStats({});
      if (statsData && (statsData.role === 'Admin' || statsData.role === 'Coordinator')) {
        setIsAdmin(true);
      }
    };
    checkAdminStatus();
  }, [apiClient]);

  const handleEditGuidelines = async () => {
    try {
      // Duplicate the CMS guideline for the user's group
      const response = await apiClient.duplicateCmsGuideline();
      if (response && response.guidelineId) {
        // Navigate to admin portal compliance tab with guidelineId to auto-open in View & Edit mode
        navigate(`/User/admin/guidelines#compliance?guidelineId=${response.guidelineId}`);
      } else {
        console.error('Failed to duplicate CMS guideline:', response);
      }
    } catch (error) {
      console.error('Error duplicating CMS guideline:', error);
    }
  };
  // "Scorecard" when the scorecard flag is on, "Compliance" otherwise.
  const surfaceLabel = getScoreSurfaceLabel(apiClient);
  let color = 'grey';
  const grade = scoreToGrade(score);
  if (grade === 'F') color = 'red';
  else if (grade === 'C') color = 'orange';
  else if (grade === 'B') color = '#FACC15';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        padding: '10px',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <strong style={{ fontSize: '16px', color: color }}>
          {surfaceLabel} - {grade}
        </strong>
      </div>

      <div
        style={{
          textAlign: 'left',
          marginTop: '20px',
          width: '100%',
        }}
      >
        {feedback.length > 0 && (
          <div
            style={{
              marginBottom: '16px',
              padding: '8px 12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <span>
              {criteriaSource === 'cms'
                ? 'Based on CMS guidelines'
                : `Based on ${surfaceLabel.toLowerCase()} criteria set by your admin`}
            </span>
            {isAdmin && criteriaSource === 'cms' && (
              <Button
                color="link"
                size="sm"
                onClick={handleEditGuidelines}
                style={{
                  padding: '0',
                  fontSize: '12px',
                  textDecoration: 'none',
                  color: '#007bff',
                }}
              >
                Edit
              </Button>
            )}
          </div>
        )}

        {(() => {
          // Check if feedback is requirement_evaluations structure (new format)
          const isRequirementEvaluations = 
            Array.isArray(feedback) && 
            feedback.length > 0 && 
            typeof feedback[0] === 'object' && 
            feedback[0].requirement_text !== undefined;

          if (!isRequirementEvaluations) {
            // Legacy format: handle string-based feedback (backward compatibility)
            return <p>No feedback available.</p>;
          }

          // Process requirement_evaluations
          const processEvaluations = (evaluations) => {
            return evaluations.map((evaluation, index) => {
              const status = evaluation.status?.toLowerCase() || '';
              const isFullyMet = status === 'fully met';
              const isPartiallyMet = status === 'partially met';
              const isNotApplicable = status === 'not applicable';
              const isRequired = evaluation.is_required || false;
              const requirementText = evaluation.requirement_text || '';
              const suggestion = evaluation.suggestion || '';
              const category = evaluation.category || '';

              return {
                requirementText,
                status,
                isFullyMet,
                isPartiallyMet,
                isNotApplicable,
                isRequired,
                suggestion,
                category,
                originalIndex: index,
              };
            });
          };

          const processedEvaluations = processEvaluations(feedback);

          // Sort: Required first, then non-required. Within each group, by severity (not met -> partially -> fully)
          const sortedEvaluations = [...processedEvaluations].sort((a, b) => {
            // First sort by required status (required first)
            if (a.isRequired !== b.isRequired) {
              return b.isRequired - a.isRequired; // true (1) comes before false (0)
            }
            // Then sort by status severity
            const statusRank = {
              'not met': 0,
              'partially met': 1,
              'fully met': 2,
              'not applicable': 3,
            };
            const rankA = statusRank[a.status] ?? 4;
            const rankB = statusRank[b.status] ?? 4;
            if (rankA !== rankB) return rankA - rankB;
            // Finally by original index
            return a.originalIndex - b.originalIndex;
          });

          // Helper function to render a requirement evaluation item
          const renderRequirementItem = (item, index) => {
            return (
              <div
                key={`${item.originalIndex}-${index}`}
                style={{
                  marginBottom: '8px',
                  padding: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <div style={{ marginTop: '2px', flexShrink: 0 }}>
                  {item.isFullyMet ? (
                    <span style={{ color: '#10b981', fontSize: '20px', fontWeight: 'bold' }}>✓</span>
                  ) : item.isNotApplicable ? (
                    <span style={{ color: '#9ca3af', fontSize: '20px', fontWeight: 'bold' }}>–</span>
                  ) : item.isPartiallyMet ? (
                    <span style={{ color: '#fbbf24', fontSize: '18px', fontWeight: 'bold' }}>🟡</span>
                  ) : (
                    <span style={{ color: '#ef4444', fontSize: '20px', fontWeight: 'bold' }}>✗</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', marginBottom: item.suggestion && !item.isFullyMet ? '6px' : '0' }}>
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#1f2937',
                        lineHeight: '1.5',
                      }}
                    >
                      {item.requirementText}
                    </span>
                    {item.isRequired && (
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '3px 8px',
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          borderRadius: '4px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Required
                      </span>
                    )}
                  </div>
                  {item.suggestion && !item.isFullyMet && !item.isNotApplicable && (
                    <div
                      style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#4b5563',
                        lineHeight: '1.5',
                      }}
                    >
                      {item.suggestion}
                    </div>
                  )}
                </div>
              </div>
            );
          };

          if (sortedEvaluations.length === 0) {
            return <p>No requirements to display.</p>;
          }

          // Group by status for display (order preserved from sortedEvaluations)
          const notMetItems = sortedEvaluations.filter(
            (item) => item.status === 'not met'
          );
          const partiallyMetItems = sortedEvaluations.filter(
            (item) => item.status === 'partially met'
          );
          const fullyMetItems = sortedEvaluations.filter(
            (item) => item.status === 'fully met'
          );
          // Conditional rules whose condition did not come up in this visit.
          const notApplicableItems = sortedEvaluations.filter(
            (item) => item.status === 'not applicable'
          );

          return (
            <div style={{ marginTop: '16px' }}>
              {notMetItems.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      marginBottom: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Not Met ({notMetItems.length})
                  </div>
                  {notMetItems.map((item, index) => renderRequirementItem(item, index))}
                </div>
              )}
              {partiallyMetItems.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      marginBottom: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Partially Met ({partiallyMetItems.length})
                  </div>
                  {partiallyMetItems.map((item, index) => renderRequirementItem(item, index))}
                </div>
              )}
              {fullyMetItems.length > 0 && (
                <div>
                  <div
                    style={{
                      marginBottom: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Fully Met ({fullyMetItems.length})
                  </div>
                  {fullyMetItems.map((item, index) => renderRequirementItem(item, index))}
                </div>
              )}
              {notApplicableItems.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div
                    style={{
                      marginBottom: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Not Applicable ({notApplicableItems.length})
                  </div>
                  {notApplicableItems.map((item, index) => renderRequirementItem(item, index))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ComplianceScore;
